import "@/i18n/config";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { WagmiProvider } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { wagmiConfig } from "@/lib/wagmi";
import Landing from "@/pages/Landing";
import LegalPage from "@/pages/LegalPage";
import { LEGAL_ORDER } from "@/data/legal";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "@/i18n/config";
import CookieConsent from "@/components/CookieConsent";

const queryClient = new QueryClient();

/**
 * LangGate — reads :lang from URL, syncs i18n + <html lang>, updates meta tags.
 */
const LangGate = () => {
    const { lang } = useParams();
    const { i18n, t } = useTranslation();

    useEffect(() => {
        if (!SUPPORTED_LANGS.includes(lang)) return;
        if (i18n.language !== lang) i18n.changeLanguage(lang);
        try {
            localStorage.setItem("aima_lang", lang);
        } catch (_) { /* noop */ }
        document.documentElement.lang = lang;

        // hreflang alternates
        const origin = window.location.origin;
        const path = window.location.pathname.replace(/^\/(en|ro)/, "");

        // Only manage landing meta here; legal pages set their own title/description.
        const isLegal = LEGAL_ORDER.some((s) => path === `/${s}`);
        if (!isLegal) {
            document.title = t("meta.title");
            const desc = document.querySelector('meta[name="description"]');
            if (desc) desc.setAttribute("content", t("meta.description"));
        }

        document.querySelectorAll('link[rel="alternate"][data-i18n="1"]').forEach((n) => n.remove());
        SUPPORTED_LANGS.forEach((l) => {
            const link = document.createElement("link");
            link.rel = "alternate";
            link.hreflang = l;
            link.href = `${origin}/${l}${path}`;
            link.setAttribute("data-i18n", "1");
            document.head.appendChild(link);
        });
        const xd = document.createElement("link");
        xd.rel = "alternate";
        xd.hreflang = "x-default";
        xd.href = `${origin}/en${path}`;
        xd.setAttribute("data-i18n", "1");
        document.head.appendChild(xd);
    }, [lang, i18n, t]);

    if (!SUPPORTED_LANGS.includes(lang)) {
        // Removed/unknown language prefixes (e.g. legacy /de links) fall back to EN, preserving the sub-path.
        const rest = window.location.pathname.replace(/^\/[^/]+/, "");
        return <Navigate to={`/${DEFAULT_LANG}${rest}${window.location.hash || ""}`} replace />;
    }
    return <Outlet />;
};

const RootRedirect = () => {
    const loc = useLocation();
    let lang = DEFAULT_LANG;
    try {
        const stored = localStorage.getItem("aima_lang");
        if (stored && SUPPORTED_LANGS.includes(stored)) lang = stored;
        else {
            const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
            if (SUPPORTED_LANGS.includes(nav)) lang = nav;
        }
    } catch (_) { /* noop */ }
    return <Navigate to={`/${lang}${loc.hash || ""}`} replace />;
};

function App() {
    return (
        <div className="App">
            <WagmiProvider config={wagmiConfig}>
                <QueryClientProvider client={queryClient}>
                    <BrowserRouter>
                        <Routes>
                            <Route path="/" element={<RootRedirect />} />
                            <Route path="/:lang" element={<LangGate />}>
                                <Route index element={<Landing />} />
                                {LEGAL_ORDER.map((slug) => (
                                    <Route key={slug} path={slug} element={<LegalPage slug={slug} />} />
                                ))}
                            </Route>
                            <Route path="*" element={<Navigate to="/en" replace />} />
                        </Routes>
                    </BrowserRouter>
                    <CookieConsent />
                </QueryClientProvider>
            </WagmiProvider>
        </div>
    );
}

export default App;
